import { getAccessToken } from "./auth";

export type LiveRole = "TUTOR" | "CLIENT" | "LEARNER";
export type LessonStatus = "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "CANCELED";

export interface LiveTutor {
  id: string;
  name: string | null;
  email: string | null;
  level?: string | null;
  goal?: string | null;
}

export interface AvailabilitySlot {
  id: string;
  starts_at: string;
  ends_at: string;
  recurring: boolean;
}

export interface BookedWindow {
  start: string;
  end: string;
}

export interface LiveLesson {
  id: string;
  tutor_id: string;
  client_id: string;
  tutor: { id: string; name: string | null; email: string | null } | null;
  client: { id: string; name: string | null; email: string | null } | null;
  scheduled_at: string;
  duration_min: number;
  status: LessonStatus;
  livekit_room: string;
  started_at: string | null;
  ended_at: string | null;
  notes_tutor: string | null;
  created_at: string;
}

export interface RoomTokenResponse {
  token: string;
  url: string;
  room: string;
  role: "tutor" | "client";
  duration_min: number;
  scheduled_at: string;
}

function fetchOpts(extra: RequestInit = {}): RequestInit {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    ...((extra.headers as Record<string, string>) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return { credentials: "include", ...extra, headers };
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    ...fetchOpts(),
    method,
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 300)}`);
  }
  return (await res.json()) as T;
}

export const liveApi = {
  setRole: (role: "TUTOR" | "CLIENT") =>
    api<{ ok: true; role: string }>("POST", "/api/live/role", { role }),

  listTutors: () =>
    api<{ tutors: LiveTutor[] }>("GET", "/api/live/tutors"),

  getAvailability: (tutorId: string, from?: Date, to?: Date) => {
    const params = new URLSearchParams();
    if (from) params.set("from", from.toISOString());
    if (to) params.set("to", to.toISOString());
    const q = params.toString();
    return api<{ slots: AvailabilitySlot[]; booked: BookedWindow[] }>(
      "GET",
      `/api/live/tutors/${tutorId}/availability${q ? "?" + q : ""}`,
    );
  },

  addAvailability: (startsAt: Date, endsAt: Date, recurring = false) =>
    api<AvailabilitySlot>("POST", "/api/live/availability", {
      starts_at: startsAt.toISOString(),
      ends_at: endsAt.toISOString(),
      recurring,
    }),

  deleteAvailability: (id: string) =>
    api<{ ok: true }>("DELETE", `/api/live/availability/${id}`),

  bookLesson: (tutorId: string, scheduledAt: Date, durationMin: number) =>
    api<LiveLesson>("POST", "/api/live/lessons", {
      tutor_id: tutorId,
      scheduled_at: scheduledAt.toISOString(),
      duration_min: durationMin,
    }),

  myLessons: () =>
    api<{ lessons: LiveLesson[] }>("GET", "/api/live/lessons/mine"),

  getLesson: (id: string) =>
    api<LiveLesson>("GET", `/api/live/lessons/${id}`),

  cancelLesson: (id: string) =>
    api<{ ok: true }>("POST", `/api/live/lessons/${id}/cancel`),

  getToken: (id: string) =>
    api<RoomTokenResponse>("POST", `/api/live/lessons/${id}/token`),

  endLesson: (id: string) =>
    api<{ ok: true }>("POST", `/api/live/lessons/${id}/end`),
};

// ---------- Tutor pre-lesson brief + post-lesson notes ----------

export interface LessonBrief {
  learner: {
    name: string | null;
    level: string | null;
    target_level: string | null;
    track: string | null;
    profession: string | null;
    interests: string[];
  };
  recent_summaries: Array<{ when: number; narrative: string; focus?: string }>;
  recurring_errors: Array<{ category: string; type: string; count: number; explanation: string }>;
  new_vocab_2w: string[];
  homework: { assigned: number; completed: number; recent_prompts: string[] };
  suggested_topics: string[];
  pep_talk: string;
}

export async function getLessonBrief(lessonId: string): Promise<LessonBrief | null> {
  const res = await fetch(`/api/live/lessons/${encodeURIComponent(lessonId)}/brief`, fetchOpts());
  if (!res.ok) return null;
  const { brief } = (await res.json()) as { brief: LessonBrief };
  return brief;
}

export async function saveLessonNotes(lessonId: string, notes: string): Promise<boolean> {
  const res = await fetch(`/api/live/lessons/${encodeURIComponent(lessonId)}/notes`, {
    ...fetchOpts(),
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ notes }),
  });
  return res.ok;
}
